import React, { useState } from 'react';
import { Check, Home, UploadCloud } from 'lucide-react';
import { useApi } from '../../api/useApi';
import { checkoutHostSubscription, hostSubscription } from '../../api/hostApi';
import { LoadingState, ErrorState } from '../../components/shared/Pending';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/shared/DataToolbar';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

export default function SubscriptionRequired() {
  const { user, logout, refresh } = useAuth();
  const { data, loading, error, refetch } = useApi(hostSubscription, []);
  const [busy, setBusy] = useState('');

  if (loading) return <LoadingState label="Loading subscription…" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const current = data?.host?.subscription || user?.subscription || {};
  const currentPlan = data?.plan?.key || current.plan || 'free';
  const plans = Array.isArray(data?.plans) ? data.plans : [];

  const choose = async (plan) => {
    setBusy(plan);
    try {
      const result = await checkoutHostSubscription(plan);
      if (result?.checkout_url) {
        window.location.assign(result.checkout_url);
        return;
      }
      await refresh();
      window.location.assign('/host/profile');
    } catch (e) {
      toast.error(e?.message || 'Could not update subscription.');
    } finally {
      setBusy('');
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-5 py-16">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-teal-700">StayEasy Host</p>
            <h1 className="mt-2 text-3xl font-bold text-slate-900">Choose your host plan</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Property limits and publishing permissions are enforced by the StayEasy API. Paid plans require Stripe payout setup before a property can be published.
            </p>
          </div>
          <Button variant="outline" onClick={logout}>Sign out</Button>
        </div>

        <Card className="mt-8 p-6">
          <div className="text-sm text-slate-600">
            Signed in as <span className="font-semibold text-slate-900">{user?.email}</span>
          </div>
          <div className="mt-2 text-sm text-slate-600">
            Current plan: <span className="font-semibold capitalize text-slate-900">{currentPlan}</span> · Status: <span className="font-semibold capitalize text-slate-900">{current.status || 'pending'}</span>
          </div>
        </Card>

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {plans.map((plan) => {
            const isCurrent = currentPlan === plan.key && (data?.active || current.status === 'active');
            const limitLabel = plan.property_limit == null ? 'Unlimited properties' : `${plan.property_limit} ${plan.property_limit === 1 ? 'property' : 'properties'}`;
            return (
              <Card key={plan.key} className={`p-5 flex flex-col ${isCurrent ? 'ring-2 ring-teal-600' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">{plan.name}</h2>
                    <p className="mt-1 text-2xl font-bold text-slate-900">${Number(plan.monthly_price || 0)}<span className="text-xs font-medium text-slate-500"> / month</span></p>
                  </div>
                  {isCurrent && <span className="rounded-full bg-teal-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-teal-700">Current</span>}
                </div>

                <div className="mt-5 space-y-2.5 text-sm text-slate-600 flex-1">
                  <div className="flex items-start gap-2"><Home className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" /><span>{limitLabel}</span></div>
                  <div className="flex items-start gap-2"><UploadCloud className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" /><span>{plan.can_publish ? 'Can publish after payout setup' : 'Draft only — publishing requires an upgrade'}</span></div>
                  {(plan.features || []).map((feature) => <div key={feature} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" /><span>{feature}</span></div>)}
                </div>

                <Button
                  className="mt-5 w-full bg-teal-700 hover:bg-teal-800"
                  disabled={Boolean(busy) || isCurrent}
                  onClick={() => choose(plan.key)}
                >
                  {busy === plan.key ? 'Working…' : isCurrent ? 'Current plan' : plan.monthly_price > 0 ? `Choose ${plan.name}` : 'Switch to Free'}
                </Button>
              </Card>
            );
          })}
        </div>

        <p className="mt-6 text-xs leading-relaxed text-slate-500">
          StayEasy retains a 10% platform fee from each completed booking. Stripe processing fees are deducted from the host side before the remaining booking balance is transferred to the connected Stripe account.
        </p>
      </div>
    </main>
  );
}
